import React, { useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface Notification {
    _id: string;
    message: string;
    status: 'unread' | 'read';
    createdAt: string;
    type: string;
    relatedEntityId?: string;
}

interface NotificationListProps {
    notifications: Notification[];
    isLoading: boolean;
    hasMore: boolean;
    loadMore: () => void;
    markAsRead: (id: string) => void;
}

const NotificationList: React.FC<NotificationListProps> = ({ notifications, isLoading, hasMore, loadMore, markAsRead }) => {
    const lastNotificationElementRef = useRef<HTMLDivElement | null>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);

    // Observer pour marquer une notification comme lue
    const observeUnreadNotification = useCallback((element: HTMLElement | null, notificationId: string) => {
        if (!element) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                markAsRead(notificationId);
                observer.disconnect();
            }
        }, { threshold: 1.0 });

        observer.observe(element);

        return () => observer.disconnect();
    }, [markAsRead]);

    // Observer pour charger plus de notifications
    useEffect(() => {
        const observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && !isLoading && hasMore) {
                loadMore();
            }
        }, { threshold: 0.1 });

        if (lastNotificationElementRef.current) {
            observer.observe(lastNotificationElementRef.current);
        }

        return () => observer.disconnect();
    }, [isLoading, hasMore, loadMore]);

    // Observez les notifications non lues
    useEffect(() => {
        const cleanupFunctions = notifications
            .filter(notification => notification.status === 'unread')
            .map(notification => {
                const element = document.getElementById(`notification-${notification._id}`) as HTMLDivElement | null;
                return observeUnreadNotification(element, notification._id);
            });

        return () => cleanupFunctions.forEach(cleanup => cleanup && cleanup());
    }, [notifications, observeUnreadNotification]);

    // Formatage de la date
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return 'Date non disponible';
        }
        return date.toLocaleString();
    };

    if (notifications.length === 0 && !isLoading) {
        return (
            <div className="bg-white dark:bg-mcdm-third-400 border border-gray-200 dark:border-mcdm-third-300 rounded-t-lg max-h-[320px] h-[320px] w-[225px] flex flex-col">
                <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
                    <span className="text-gray-400 dark:text-gray-500 text-sm mt-1">...zZz...</span>
                    <span className="text-sm mt-1 text-mcdm-third-500 dark:text-mcdm-primary-200">Aucune notification</span>
                </div>
                <Link
                    to="/notifications"
                    className="px-4 py-3 text-center text-sm font-medium text-mcdm-secondary-500 dark:text-mcdm-secondary-400 hover:bg-gray-50 dark:hover:bg-mcdm-third-300 border-t border-gray-200 dark:border-mcdm-third-300"
                >
                    Toutes les notifications
                </Link>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-mcdm-third-400 border border-gray-200 dark:border-mcdm-third-300 rounded-t-lg max-h-[320px] h-[320px] w-[225px] flex flex-col">
            <h2 className="pl-4 pr-4 pt-4 pb-2 text-lg font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100 text-left mb-2">Notifications</h2>

            <div className="flex-1 overflow-y-auto" ref={scrollRef}>
                {isLoading && notifications.length === 0 && (
                    <div className="p-4">
                        {Array.from({ length: 5 }).map((_, index) => (
                            <div key={index} className="animate-pulse flex flex-col space-y-4 mb-4">
                                <div className="h-4 bg-gray-300 dark:bg-mcdm-third-300 rounded"></div>
                                <div className="h-4 bg-gray-300 dark:bg-mcdm-third-300 rounded w-5/6"></div>
                            </div>
                        ))}
                    </div>
                )}

                {notifications.map((notification, index) => {
                    const dossierUrl = notification.relatedEntityId
                        ? `/dossiers/${notification.relatedEntityId}`
                        : '#';

                    const NotificationContent = () => (
                        <>
                            <div className="text-sm mb-2 font-normal text-mcdm-third-500 dark:text-mcdm-primary-200">
                                {notification.message}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                {formatDate(notification.createdAt)}
                            </div>
                        </>
                    );

                    return (
                        <div
                            key={notification._id || index}
                            id={`notification-${notification._id}`}
                            className={`pl-4 pb-4 mb-0 border-b border-gray-200 dark:border-mcdm-third-300 last:border-b-0 p-2 ${
                                notification.status === 'unread' ? 'bg-mcdm-green-100/30 dark:bg-mcdm-green-500/20' : 'bg-white dark:bg-mcdm-third-400'
                            } hover:bg-mcdm-green-100/20 dark:hover:bg-mcdm-third-300`}
                            onClick={() => {
                                if (notification.status === 'unread') {
                                    markAsRead(notification._id);
                                }
                            }}
                        >
                            {notification.relatedEntityId ? (
                                <Link to={dossierUrl}>
                                    <NotificationContent />
                                </Link>
                            ) : (
                                <NotificationContent />
                            )}
                        </div>
                    );
                })}

                {notifications.length > 0 && (
                    <div ref={lastNotificationElementRef} style={{ height: '20px' }}></div>
                )}

                {isLoading && notifications.length > 0 && (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                        Chargement...
                    </div>
                )}
            </div>

            <Link
                to="/notifications"
                className="px-4 py-3 text-center text-sm font-medium text-mcdm-secondary-500 dark:text-mcdm-secondary-400 hover:bg-gray-50 dark:hover:bg-mcdm-third-300 border-t border-gray-200 dark:border-mcdm-third-300"
            >
                Toutes les notifications
            </Link>
        </div>
    );
};

export default NotificationList;